import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom"; 
import { 
    AppBar, Toolbar, Box, Button, Menu, MenuItem, 
    Container, Stack, Typography, Divider, IconButton, Drawer, Badge
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { KeyboardArrowDown, Menu as MenuIcon } from "@mui/icons-material";
import { Logo } from "./logo";
import axios from "axios"; 
import swal from "sweetalert";

// THEME DÀNH RIÊNG CHO NAVBAR (Giữ độ mỏng nhẹ, sang trọng)
const navTheme = createTheme({
    typography: { fontFamily: "'Plus Jakarta Sans', sans-serif" },
});

export const DashboardSidebar = (props) => {
    const location = useLocation();
    const currentPath = location.pathname;
    const navigate = useNavigate(); 
    
    const [profile, setProfile] = useState(JSON.parse(localStorage.getItem("profile")));
    
    // --- STATE QUẢN LÝ MENU TRÊN MÁY TÍNH ---
    const [anchorElUser, setAnchorElUser] = useState(null);
    const [anchorElTX, setAnchorElTX] = useState(null); 
    const [anchorElXD, setAnchorElXD] = useState(null);
    const [anchorElXS, setAnchorElXS] = useState(null);
    const [anchorElHistory, setAnchorElHistory] = useState(null);
    const [anchorElSystem, setAnchorElSystem] = useState(null);

    // --- STATE QUẢN LÝ MENU ĐIỆN THOẠI ---
    const [mobileOpen, setMobileOpen] = useState(false);

    // --- STATE ĐẾM SỐ ĐƠN CHỜ DUYỆT ---
    const [pendingRechargeCount, setPendingRechargeCount] = useState(0);
    const [pendingWithdrawCount, setPendingWithdrawCount] = useState(0);

    // ==========================================
    // KIỂM TRA PHÂN QUYỀN MỚI TỪ PROFILE
    // ==========================================
    const isFullAdmin = profile?.permission?.full === 1; 
    const pRoles = profile?.roles || {}; 

    // ==========================================
    // BẢO VỆ ĐƯỜNG DẪN TỔNG (GLOBAL GUARD)
    // ==========================================
    useEffect(() => {
        // Nếu là Admin tối cao thì được đi muôn nơi, không cần kiểm tra
        if (isFullAdmin) return;

        // Định nghĩa bản đồ: Đường dẫn nào cần Quyền gì
        const routePermissions = {
            '/admin/add': pRoles.recharge,
            '/admin/request': pRoles.withdraw,
            '/admin/users': pRoles.user_customer,
            '/admin/allNV': pRoles.user_admin,
            '/admin/settx45s': pRoles.game_tx45,
            '/admin/settx60s': pRoles.game_tx60,
            '/admin/setxd45s': pRoles.game_xd45,
            '/admin/setxd60s': pRoles.game_xd60,
            '/admin/setxs75s': pRoles.game_xs75,
            '/admin/setxs120s': pRoles.game_xs120,
            '/admin/history': pRoles.hist_bet,
            '/admin/historyapi': pRoles.hist_api,
            '/admin/promo': pRoles.sys_promo,
            '/admin/refund': pRoles.hist_refund,
            '/admin/AdminCustomLottery': pRoles.sys_AdminCustomLottery,
            '/admin/settingnotify': pRoles.sys_notify,
            
            // CÁC QUYỀN HỆ THỐNG ĐÃ ĐƯỢC MỞ KHÓA VÀ CHECK THEO OBJECT ROLES
            '/admin/WithdrawAdmin': pRoles.sys_withdraw, 
            '/admin/setting': pRoles.sys_odds, 
            '/admin/setting/profit': pRoles.sys_vip, 
            '/admin/bank': pRoles.sys_bank, 
            '/admin/config': pRoles.sys_config, 
        };

        // Nếu currentPath nằm trong danh sách kiểm tra, VÀ quyền trả về false -> Đuổi
        if (routePermissions[currentPath] === false) {
            swal("Truy cập bị từ chối!", "Tài khoản của bạn không được cấp quyền để xem nội dung này.", "error");
            navigate("/admin");
        }
    }, [currentPath, isFullAdmin, pRoles, navigate]);

    useEffect(() => {
        const timer = setInterval(() => {
            const storedProfile = JSON.parse(localStorage.getItem("profile"));
            if (storedProfile && !profile) {
                setProfile(storedProfile);
                clearInterval(timer); 
            }
        }, 1000); 
        return () => clearInterval(timer);
    }, [profile]);

    // FETCH TỰ ĐỘNG LẤY SỐ LƯỢNG ĐƠN NẠP VÀ RÚT PENDING
    useEffect(() => {
        const fetchPendingCounts = () => {
            if (isFullAdmin || pRoles.recharge) {
                axios.get(`${process.env.REACT_APP_API_URL}/payment/nap?page=1&limit=100`)
                    .then((res) => {
                        const count = (res.data.data || []).filter(item => item.status_payment === "Pending").length;
                        setPendingRechargeCount(count);
                    }).catch(() => {});
            }

            if (isFullAdmin || pRoles.withdraw) {
                axios.get(`${process.env.REACT_APP_API_URL}/payment/rut?page=1&limit=100`)
                    .then((res) => {
                        const count = (res.data.data || []).filter(item => item.status_payment === "Pending").length;
                        setPendingWithdrawCount(count);
                    }).catch(() => {});
            }
        };

        fetchPendingCounts(); 
        const intervalId = setInterval(fetchPendingCounts, 30000); 
        return () => clearInterval(intervalId);
    }, [isFullAdmin, pRoles.recharge, pRoles.withdraw]);

    const handleCloseAll = () => {
        setAnchorElUser(null);
        setAnchorElTX(null);
        setAnchorElXD(null);
        setAnchorElXS(null);
        setAnchorElHistory(null);
        setAnchorElSystem(null);
    };

    // ==========================================
    // HÀM TẠO STYLE CHO NÚT MENU (Có hiệu ứng Active)
    // ==========================================
    const getNavButtonStyle = (isActive) => ({
        color: isActive ? '#1B84FF' : '#4B5675',
        fontWeight: isActive ? 800 : 600,
        textTransform: 'none',
        fontSize: '0.9rem', 
        px: 1.5,
        py: 1,
        borderRadius: '8px',
        whiteSpace: 'nowrap', 
        minWidth: 'max-content',
        backgroundColor: isActive ? '#F1FAFF' : 'transparent',
        transition: 'all 0.2s',
        '&:hover': { color: '#1B84FF', backgroundColor: '#F1FAFF' } 
    });

    const menuItemStyle = {
        py: 1.2, px: 3, fontSize: '0.85rem', fontWeight: 600, color: '#4B5675', whiteSpace: 'nowrap',
        '&:hover': { backgroundColor: '#F1FAFF', color: '#1B84FF' }
    };

    // --- COMPONENT HỖ TRỢ CHO MENU ĐIỆN THOẠI ---
    const MobileNavGroup = ({ title, children }) => (
        <Box sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ color: '#A1A5B7', fontWeight: 800, px: 2, mb: 0.5, display: 'block', textTransform: 'uppercase' }}>
                {title}
            </Typography>
            <Stack spacing={0.5}>{children}</Stack>
        </Box>
    );

    const MobileNavItem = ({ to, title, badgeCount }) => {
        const isActive = currentPath === to;
        return (
            <Button component={Link} to={to} onClick={() => setMobileOpen(false)}
                sx={{
                    justifyContent: 'flex-start', color: isActive ? '#1B84FF' : '#4B5675', 
                    fontWeight: isActive ? 800 : 600, textTransform: 'none',
                    px: 2, py: 1, borderRadius: '8px', mx: 1,
                    backgroundColor: isActive ? '#F1FAFF' : 'transparent',
                    '&:hover': { backgroundColor: '#F1FAFF', color: '#1B84FF' }
                }}
            >
                {badgeCount > 0 ? (
                    <Badge badgeContent={badgeCount} color="error" sx={{ '& .MuiBadge-badge': { right: -15, top: 10 } }}>
                        {title}
                    </Badge>
                ) : (
                    title
                )}
            </Button>
        );
    };

    return (
        <ThemeProvider theme={navTheme}>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
                * { font-family: 'Plus Jakarta Sans', sans-serif; }
            `}</style>

            <AppBar position="sticky" elevation={0} sx={{ backgroundColor: '#FFFFFF', borderBottom: '1px solid #E4E6EF', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
                <Container maxWidth="xl" sx={{ px: { xs: 2, xl: 5 } }}> 
                    <Toolbar disableGutters sx={{ minHeight: '70px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        
                        {/* === KHU VỰC TRÁI: LOGO === */}
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <IconButton
                                color="inherit"
                                aria-label="open drawer"
                                edge="start"
                                onClick={() => setMobileOpen(true)}
                                sx={{ mr: 1, display: { lg: 'none' }, color: '#252F4A' }}
                            >
                                <MenuIcon />
                            </IconButton>
                            <Box component={Link} to="/" sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
                                <Logo sx={{ height: 35, width: 35 }} />
                            </Box>
                        </Box>

                        {/* === KHU VỰC GIỮA: CÁC MENU === */}
                        <Box sx={{ display: { xs: 'none', lg: 'flex' }, flexGrow: 1, justifyContent: 'center', alignItems: 'center', gap: 1 }}>
                            
                            {/* TỔNG QUAN */}
                            {(isFullAdmin || pRoles.dashboard) && (
                                <Button component={Link} to="/admin" sx={getNavButtonStyle(currentPath === '/admin')}>Tổng Quan</Button>
                            )}

                            {/* NẠP TIỀN */}
                            {(isFullAdmin || pRoles.recharge) && (
                                <Badge badgeContent={pendingRechargeCount} color="error" sx={{ '& .MuiBadge-badge': { right: 8, top: 10, fontWeight: 'bold' } }}>
                                    <Button component={Link} to="/admin/add" sx={getNavButtonStyle(currentPath === '/admin/add')}>Nạp Tiền</Button>
                                </Badge>
                            )}

                            {/* RÚT TIỀN */}
                            {(isFullAdmin || pRoles.withdraw) && (
                                <Badge badgeContent={pendingWithdrawCount} color="error" sx={{ '& .MuiBadge-badge': { right: 8, top: 10, fontWeight: 'bold' } }}>
                                    <Button component={Link} to="/admin/request" sx={getNavButtonStyle(currentPath === '/admin/request')}>Rút Tiền</Button>
                                </Badge>
                            )}

                            {/* KHÁCH HÀNG & NHÂN VIÊN */}
                            {(isFullAdmin || pRoles.users_manage || pRoles.user_customer || pRoles.user_admin) && (
                                <>
                                    <Button 
                                        sx={getNavButtonStyle(currentPath === '/admin/users' || currentPath === '/admin/allNV')} 
                                        endIcon={<KeyboardArrowDown />} 
                                        onClick={(e) => setAnchorElUser(e.currentTarget)}
                                    >
                                        Người Dùng
                                    </Button>
                                    <Menu anchorEl={anchorElUser} open={Boolean(anchorElUser)} onClose={handleCloseAll} PaperProps={{ sx: { mt: 2, borderRadius: '8px', minWidth: 200, boxShadow: '0px 10px 30px rgba(0,0,0,0.08)' } }}>
                                        {(isFullAdmin || pRoles.user_customer) && (
                                            <MenuItem component={Link} to="/admin/users" onClick={handleCloseAll} sx={{...menuItemStyle, color: currentPath === '/admin/users' ? '#1B84FF' : '#4B5675'}}>Khách Hàng</MenuItem>
                                        )}
                                        
                                        {(isFullAdmin || pRoles.user_admin) && (
                                            <MenuItem component={Link} to="/admin/allNV" onClick={handleCloseAll} sx={{...menuItemStyle, color: currentPath === '/admin/allNV' ? '#1B84FF' : '#4B5675'}}>Đại Lý / Nhân Viên</MenuItem>
                                        )}
                                    </Menu>
                                </>
                            )}

                            {/* TÀI XỈU */}
                            {(isFullAdmin || pRoles.game_tx45 || pRoles.game_tx60) && (
                                <>
                                    <Button 
                                        sx={getNavButtonStyle(currentPath.includes('settx'))} 
                                        endIcon={<KeyboardArrowDown />} 
                                        onClick={(e) => setAnchorElTX(e.currentTarget)}
                                    >
                                        Tài Xỉu
                                    </Button>
                                    <Menu anchorEl={anchorElTX} open={Boolean(anchorElTX)} onClose={handleCloseAll} PaperProps={{ sx: { mt: 2, borderRadius: '8px', minWidth: 160, boxShadow: '0px 10px 30px rgba(0,0,0,0.08)' } }}>
                                        {(isFullAdmin || pRoles.game_tx45) && <MenuItem component={Link} to="/admin/settx45s" onClick={handleCloseAll} sx={{...menuItemStyle, color: currentPath === '/admin/settx45s' ? '#1B84FF' : '#4B5675'}}>Tài Xỉu 45s</MenuItem>}
                                        {(isFullAdmin || pRoles.game_tx60) && <MenuItem component={Link} to="/admin/settx60s" onClick={handleCloseAll} sx={{...menuItemStyle, color: currentPath === '/admin/settx60s' ? '#1B84FF' : '#4B5675'}}>Tài Xỉu 60s</MenuItem>}
                                    </Menu>
                                </>
                            )}

                            {/* XÓC ĐĨA */}
                            {(isFullAdmin || pRoles.game_xd45 || pRoles.game_xd60) && (
                                <>
                                    <Button 
                                        sx={getNavButtonStyle(currentPath.includes('setxd'))} 
                                        endIcon={<KeyboardArrowDown />} 
                                        onClick={(e) => setAnchorElXD(e.currentTarget)}
                                    >
                                        Xóc Đĩa
                                    </Button>
                                    <Menu anchorEl={anchorElXD} open={Boolean(anchorElXD)} onClose={handleCloseAll} PaperProps={{ sx: { mt: 2, borderRadius: '8px', minWidth: 160, boxShadow: '0px 10px 30px rgba(0,0,0,0.08)' } }}>
                                        {(isFullAdmin || pRoles.game_xd45) && <MenuItem component={Link} to="/admin/setxd45s" onClick={handleCloseAll} sx={{...menuItemStyle, color: currentPath === '/admin/setxd45s' ? '#1B84FF' : '#4B5675'}}>Xóc Đĩa 45s</MenuItem>}
                                        {(isFullAdmin || pRoles.game_xd60) && <MenuItem component={Link} to="/admin/setxd60s" onClick={handleCloseAll} sx={{...menuItemStyle, color: currentPath === '/admin/setxd60s' ? '#1B84FF' : '#4B5675'}}>Xóc Đĩa 60s</MenuItem>}
                                    </Menu>
                                </>
                            )}

                            {/* XỔ SỐ */}
                            {(isFullAdmin || pRoles.game_xs75 || pRoles.game_xs120 || pRoles.sys_AdminCustomLottery) && (
                                <>
                                    <Button 
                                        sx={getNavButtonStyle(currentPath.includes('setxs') || currentPath === '/admin/AdminCustomLottery')} 
                                        endIcon={<KeyboardArrowDown />} 
                                        onClick={(e) => setAnchorElXS(e.currentTarget)}
                                    >
                                        Xổ Số
                                    </Button>
                                    <Menu anchorEl={anchorElXS} open={Boolean(anchorElXS)} onClose={handleCloseAll} PaperProps={{ sx: { mt: 2, borderRadius: '8px', minWidth: 160, boxShadow: '0px 10px 30px rgba(0,0,0,0.08)' } }}>
                                        {(isFullAdmin || pRoles.sys_AdminCustomLottery) && <MenuItem component={Link} to="/admin/AdminCustomLottery" onClick={handleCloseAll} sx={{...menuItemStyle, color: currentPath === '/admin/AdminCustomLottery' ? '#1B84FF' : '#4B5675'}}>Xổ Số Ba Miền</MenuItem>}
                                        {(isFullAdmin || pRoles.game_xs75) && <MenuItem component={Link} to="/admin/setxs75s" onClick={handleCloseAll} sx={{...menuItemStyle, color: currentPath === '/admin/setxs75s' ? '#1B84FF' : '#4B5675'}}>Xổ Số Siêu tốc 75s</MenuItem>}
                                        {(isFullAdmin || pRoles.game_xs120) && <MenuItem component={Link} to="/admin/setxs120s" onClick={handleCloseAll} sx={{...menuItemStyle, color: currentPath === '/admin/setxs120s' ? '#1B84FF' : '#4B5675'}}>Xổ Số Siêu tốc 120s</MenuItem>}
                                    </Menu>
                                </>
                            )}

                            {/* LỊCH SỬ CHƠI */}
                            {(isFullAdmin || pRoles.hist_bet || pRoles.hist_api || pRoles.hist_refund) && (
                                <>
                                    <Button 
                                        sx={getNavButtonStyle(currentPath === '/admin/history' || currentPath === '/admin/refund' || currentPath === '/admin/historyapi')} 
                                        endIcon={<KeyboardArrowDown />} 
                                        onClick={(e) => setAnchorElHistory(e.currentTarget)}
                                    >
                                        Lịch Sử
                                    </Button>
                                    <Menu anchorEl={anchorElHistory} open={Boolean(anchorElHistory)} onClose={handleCloseAll} PaperProps={{ sx: { mt: 2, borderRadius: '8px', minWidth: 160, boxShadow: '0px 10px 30px rgba(0,0,0,0.08)' } }}>
                                        {(isFullAdmin || pRoles.hist_bet) && <MenuItem component={Link} to="/admin/history" onClick={handleCloseAll} sx={{...menuItemStyle, color: currentPath === '/admin/history' ? '#1B84FF' : '#4B5675'}}>Lịch sử Cược</MenuItem>}
                                        {(isFullAdmin || pRoles.hist_api) && <MenuItem component={Link} to="/admin/historyapi" onClick={handleCloseAll} sx={{...menuItemStyle, color: currentPath === '/admin/historyapi' ? '#1B84FF' : '#4B5675'}}>Lịch sử API</MenuItem>}
                                        {(isFullAdmin || pRoles.hist_refund) && <MenuItem component={Link} to="/admin/refund" onClick={handleCloseAll} sx={{...menuItemStyle, color: currentPath === '/admin/refund' ? '#1B84FF' : '#4B5675'}}>Hoàn trả</MenuItem>}
                                    </Menu>
                                </>
                            )}

                         {/* HỆ THỐNG */}
                            {(isFullAdmin || pRoles.sys_promo || pRoles.sys_notify || pRoles.sys_withdraw || pRoles.sys_odds || pRoles.sys_vip || pRoles.sys_bank || pRoles.sys_config) && (
                                <>
                                    <Button 
                                        sx={getNavButtonStyle(currentPath === '/admin/promo' || currentPath === '/admin/WithdrawAdmin' || currentPath === '/admin/settingnotify' || currentPath === '/admin/setting' || currentPath === '/admin/setting/profit' || currentPath === '/admin/bank' || currentPath === '/admin/config')} 
                                        endIcon={<KeyboardArrowDown />} 
                                        onClick={(e) => setAnchorElSystem(e.currentTarget)}
                                    >
                                        Cài Đặt
                                    </Button>
                                    <Menu anchorEl={anchorElSystem} open={Boolean(anchorElSystem)} onClose={handleCloseAll} PaperProps={{ sx: { mt: 2, borderRadius: '8px', minWidth: 200, boxShadow: '0px 10px 30px rgba(0,0,0,0.08)' } }}>
                                        {(isFullAdmin || pRoles.sys_promo) && <MenuItem component={Link} to="/admin/promo" onClick={handleCloseAll} sx={{...menuItemStyle, color: currentPath === '/admin/promo' ? '#1B84FF' : '#4B5675'}}>Khuyến mãi</MenuItem>}
                                        {(isFullAdmin || pRoles.sys_notify) && <MenuItem component={Link} to="/admin/settingnotify" onClick={handleCloseAll} sx={{...menuItemStyle, color: currentPath === '/admin/settingnotify' ? '#1B84FF' : '#4B5675'}}>Thông báo</MenuItem>}
                                        
                                        <Divider sx={{ my: 1 }} />
                                        {(isFullAdmin || pRoles.sys_withdraw) && <MenuItem component={Link} to="/admin/WithdrawAdmin" onClick={handleCloseAll} sx={{...menuItemStyle, color: currentPath === '/admin/WithdrawAdmin' ? '#1B84FF' : '#4B5675'}}>Rút quỹ hệ thống</MenuItem>}
                                        {(isFullAdmin || pRoles.sys_odds) && <MenuItem component={Link} to="/admin/setting" onClick={handleCloseAll} sx={{...menuItemStyle, color: currentPath === '/admin/setting' ? '#1B84FF' : '#4B5675'}}>Tỷ lệ Cược</MenuItem>}
                                        {(isFullAdmin || pRoles.sys_vip) && <MenuItem component={Link} to="/admin/setting/profit" onClick={handleCloseAll} sx={{...menuItemStyle, color: currentPath === '/admin/setting/profit' ? '#1B84FF' : '#4B5675'}}>Cấp độ VIP</MenuItem>}
                                        {(isFullAdmin || pRoles.sys_bank) && <MenuItem component={Link} to="/admin/bank" onClick={handleCloseAll} sx={{...menuItemStyle, color: currentPath === '/admin/bank' ? '#1B84FF' : '#4B5675'}}>Ngân hàng nhận</MenuItem>}
                                        {(isFullAdmin || pRoles.sys_config) && <MenuItem component={Link} to="/admin/config" onClick={handleCloseAll} sx={{...menuItemStyle, color: currentPath === '/admin/config' ? '#1B84FF' : '#4B5675'}}>Cấu hình Chung</MenuItem>}
                                    </Menu>
                                </>
                            )}
                        </Box>

                        {/* === KHU VỰC BÊN PHẢI: ĐĂNG XUẤT === */}
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Button variant="outlined" 
                                onClick={() => {
                                    localStorage.removeItem("user"); localStorage.removeItem("profile"); localStorage.removeItem("data"); window.location.href = "/";
                                }}
                                sx={{ 
                                    display: { xs: 'none', sm: 'flex' }, 
                                    color: '#F1416C', borderColor: '#F1416C', 
                                    textTransform: 'none', fontWeight: 700, borderRadius: '8px', px: 3, 
                                    whiteSpace: 'nowrap', 
                                    '&:hover': { borderColor: '#F1416C', backgroundColor: '#FFF5F8' } 
                                }}
                            >
                                Đăng xuất
                            </Button>
                        </Box>

                    </Toolbar>
                </Container>

                {/* ======================================================== */}
                {/* DRAWER MENU TRƯỢT DÀNH RIÊNG CHO ĐIỆN THOẠI              */}
                {/* ======================================================== */}
                <Drawer
                    variant="temporary"
                    anchor="left"
                    open={mobileOpen}
                    onClose={() => setMobileOpen(false)}
                    ModalProps={{ keepMounted: true }} 
                    sx={{
                        display: { xs: 'block', lg: 'none' },
                        '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 280, backgroundColor: '#FFFFFF' },
                    }}
                >
                    <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Logo sx={{ height: 30, width: 30 }} />
                    </Box>
                    <Divider sx={{ mb: 2, borderColor: '#F4F6FA' }} />
                    
                    <Box sx={{ flexGrow: 1, overflowY: 'auto', pb: 4 }}>
                        
                        {(isFullAdmin || pRoles.dashboard) && (
                            <MobileNavGroup title="Bảng Điều Khiển">
                                <MobileNavItem to="/admin" title="Tổng Quan" />
                            </MobileNavGroup>
                        )}

                        {(isFullAdmin || pRoles.recharge || pRoles.withdraw) && (
                            <MobileNavGroup title="Giao Dịch">
                                {(isFullAdmin || pRoles.recharge) && <MobileNavItem to="/admin/add" title="Nạp Tiền" badgeCount={pendingRechargeCount} />}
                                {(isFullAdmin || pRoles.withdraw) && <MobileNavItem to="/admin/request" title="Rút Tiền" badgeCount={pendingWithdrawCount} />}
                            </MobileNavGroup>
                        )}

                        {(isFullAdmin || pRoles.user_customer || pRoles.user_admin) && (
                            <MobileNavGroup title="Khách Hàng & Đại Lý">
                                {(isFullAdmin || pRoles.user_customer) && <MobileNavItem to="/admin/users" title="Danh sách Khách" />}
                                {(isFullAdmin || pRoles.user_admin) && <MobileNavItem to="/admin/allNV" title="Nhân viên / Đại lý" />}
                            </MobileNavGroup>
                        )}

                        {/* HIỂN THỊ CỤM GAME NẾU CÓ ÍT NHẤT 1 QUYỀN GAME ĐƯỢC BẬT */}
                        {(isFullAdmin || pRoles.game_tx45 || pRoles.game_tx60 || pRoles.game_xd45 || pRoles.game_xd60 || pRoles.game_xs75 || pRoles.game_xs120 || pRoles.sys_AdminCustomLottery) && (
                            <MobileNavGroup title="Quản Lý Trò Chơi">
                                {(isFullAdmin || pRoles.game_tx45 || pRoles.game_tx60) && (
                                    <>
                                        <Typography variant="body2" sx={{ px: 3, py: 1, color: '#252F4A', fontWeight: 'bold' }}>Tài Xỉu</Typography>
                                        <Box sx={{ pl: 1 }}>
                                            {(isFullAdmin || pRoles.game_tx45) && <MobileNavItem to="/admin/settx45s" title="Tài Xỉu 45s" />}
                                            {(isFullAdmin || pRoles.game_tx60) && <MobileNavItem to="/admin/settx60s" title="Tài Xỉu 60s" />}
                                        </Box>
                                    </>
                                )}
                                
                                {(isFullAdmin || pRoles.game_xd45 || pRoles.game_xd60) && (
                                    <>
                                        <Typography variant="body2" sx={{ px: 3, py: 1, mt: 1, color: '#252F4A', fontWeight: 'bold' }}>Xóc Đĩa</Typography>
                                        <Box sx={{ pl: 1 }}>
                                            {(isFullAdmin || pRoles.game_xd45) && <MobileNavItem to="/admin/setxd45s" title="Xóc Đĩa 45s" />}
                                            {(isFullAdmin || pRoles.game_xd60) && <MobileNavItem to="/admin/setxd60s" title="Xóc Đĩa 60s" />}
                                        </Box>
                                    </>
                                )}

                                {(isFullAdmin || pRoles.game_xs75 || pRoles.game_xs120 || pRoles.sys_AdminCustomLottery) && (
                                    <>
                                        <Typography variant="body2" sx={{ px: 3, py: 1, mt: 1, color: '#252F4A', fontWeight: 'bold' }}>Xổ Số</Typography>
                                        <Box sx={{ pl: 1 }}>
                                            {(isFullAdmin || pRoles.sys_AdminCustomLottery) && <MobileNavItem to="/admin/AdminCustomLottery" title="Xổ Số Ba Miền" />}
                                            {(isFullAdmin || pRoles.game_xs75) && <MobileNavItem to="/admin/setxs75s" title="Xổ Số Siêu tốc 75s" />}
                                            {(isFullAdmin || pRoles.game_xs120) && <MobileNavItem to="/admin/setxs120s" title="Xổ Số Siêu tốc 120s" />}
                                        </Box>
                                    </>
                                )}
                            </MobileNavGroup>
                        )}

                        {(isFullAdmin || pRoles.hist_bet || pRoles.hist_api || pRoles.hist_refund) && (
                            <MobileNavGroup title="Lịch Sử">
                                {(isFullAdmin || pRoles.hist_bet) && <MobileNavItem to="/admin/history" title="Lịch sử Cược" />}
                                {(isFullAdmin || pRoles.hist_api) && <MobileNavItem to="/admin/historyapi" title="Lịch sử API" />}
                                {(isFullAdmin || pRoles.hist_refund) && <MobileNavItem to="/admin/refund" title="Hoàn trả" />}
                            </MobileNavGroup>
                        )}

                      {(isFullAdmin || pRoles.sys_promo || pRoles.sys_notify || pRoles.sys_withdraw || pRoles.sys_odds || pRoles.sys_vip || pRoles.sys_bank || pRoles.sys_config) && (
                            <MobileNavGroup title="Cài Đặt Hệ Thống">
                                {(isFullAdmin || pRoles.sys_promo) && <MobileNavItem to="/admin/promo" title="Sự kiện / Khuyến mãi" />}
                                {(isFullAdmin || pRoles.sys_notify) && <MobileNavItem to="/admin/settingnotify" title="Thông báo" />}
                                
                                {(isFullAdmin || pRoles.sys_withdraw) && <MobileNavItem to="/admin/WithdrawAdmin" title="Rút quỹ hệ thống" />}
                                {(isFullAdmin || pRoles.sys_odds) && <MobileNavItem to="/admin/setting" title="Tỷ lệ Cược" />}
                                {(isFullAdmin || pRoles.sys_vip) && <MobileNavItem to="/admin/setting/profit" title="Cấp độ VIP" />}
                                {(isFullAdmin || pRoles.sys_bank) && <MobileNavItem to="/admin/bank" title="Ngân hàng nhận" />}
                                {(isFullAdmin || pRoles.sys_config) && <MobileNavItem to="/admin/config" title="Cấu hình Chung" />}
                            </MobileNavGroup>
                        )}
                        <Divider sx={{ my: 2, borderColor: '#F4F6FA' }} />
                        <Button 
                            fullWidth
                            onClick={() => {
                                localStorage.removeItem("user"); localStorage.removeItem("profile"); localStorage.removeItem("data"); window.location.href = "/";
                            }}
                            sx={{ color: '#F1416C', fontWeight: 800, textTransform: 'none', justifyContent: 'flex-start', px: 3, '&:hover': { backgroundColor: '#FFF5F8' } }}
                        >
                            Đăng xuất
                        </Button>
                    </Box>
                </Drawer>

            </AppBar>
        </ThemeProvider>
    );
};